'use client';

import React, { useState } from 'react';
import { 
  Receipt, 
  Clock, 
  PackageCheck, 
  Ban, 
  Eye, 
  EyeOff 
} from 'lucide-react';

interface ChannelSummary {
  EFECTIVO: number;
  DEBITO: number;
  CREDITO: number;
  TRANSFERENCIA: number;
  MERCADOPAGO: number;
  OTRO: number;
}

interface SalesKpiStripProps {
  totalSales: number;
  pendingSales: number;
  deliveredSales: number;
  cancelledSales: number;
  grandTotal: number;
  channels: ChannelSummary;
  selectedFilterState?: string;
  onSelectFilterState?: (state: string) => void;
}

export default function SalesKpiStrip({
  totalSales,
  pendingSales,
  deliveredSales,
  cancelledSales,
  grandTotal,
  channels,
  selectedFilterState = 'ALL',
  onSelectFilterState
}: SalesKpiStripProps) {
  const [showAmounts, setShowAmounts] = useState(true);

  const formatAmount = (val: number) => {
    if (!showAmounts) return '$ •••••';
    if (!val || val === 0) return '—';
    return `$ ${val.toLocaleString('es-AR')}`;
  };

  const formatGrandTotal = (val: number) => {
    if (!showAmounts) return '$ •••••';
    return `$ ${val.toLocaleString('es-AR')}`;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
      {/* 4 Cards de Pedidos (5 columnas en lg) */}
      <div className="lg:col-span-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* 1. VENTAS (Total) */}
        <div 
          onClick={() => onSelectFilterState && onSelectFilterState('ALL')}
          className={`relative bg-white rounded-2xl p-4 border transition cursor-pointer overflow-hidden shadow-sm hover:shadow-md ${
            selectedFilterState === 'ALL'
              ? 'border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-50/20'
              : 'border-slate-200/90 hover:border-slate-300'
          }`}
        >
          <div className="flex flex-col justify-between h-full relative z-10">
            <span className="text-[11px] font-bold tracking-wider text-slate-500 uppercase">
              Ventas
            </span>
            <span className="text-2xl md:text-3xl font-extrabold text-slate-800 mt-2">
              {totalSales}
            </span>
          </div>
          {/* Marca de agua */}
          <div className="absolute right-2.5 bottom-2.5 opacity-15 text-emerald-800 pointer-events-none">
            <Receipt className="w-12 h-12 stroke-[1.2]" />
          </div>
        </div>

        {/* 2. PENDIENTE */}
        <div 
          onClick={() => onSelectFilterState && onSelectFilterState('PENDING')}
          className={`relative bg-white rounded-2xl p-4 border transition cursor-pointer overflow-hidden shadow-sm hover:shadow-md ${
            selectedFilterState === 'PENDING'
              ? 'border-amber-500 ring-2 ring-amber-500/20 bg-amber-50/20'
              : 'border-slate-200/90 hover:border-slate-300'
          }`}
        >
          <div className="flex flex-col justify-between h-full relative z-10">
            <span className="text-[11px] font-bold tracking-wider text-amber-600 uppercase">
              Pendiente
            </span>
            <span className="text-2xl md:text-3xl font-extrabold text-slate-800 mt-2">
              {pendingSales}
            </span>
          </div>
          {/* Marca de agua */}
          <div className="absolute right-2.5 bottom-2.5 opacity-15 text-amber-600 pointer-events-none">
            <Clock className="w-12 h-12 stroke-[1.2]" />
          </div>
        </div>

        {/* 3. ENTREGADA */}
        <div 
          onClick={() => onSelectFilterState && onSelectFilterState('DELIVERED')}
          className={`relative bg-white rounded-2xl p-4 border transition cursor-pointer overflow-hidden shadow-sm hover:shadow-md ${
            selectedFilterState === 'DELIVERED'
              ? 'border-emerald-600 ring-2 ring-emerald-600/20 bg-emerald-50/20'
              : 'border-slate-200/90 hover:border-slate-300'
          }`}
        >
          <div className="flex flex-col justify-between h-full relative z-10">
            <span className="text-[11px] font-bold tracking-wider text-emerald-700 uppercase">
              Entregada
            </span>
            <span className="text-2xl md:text-3xl font-extrabold text-slate-800 mt-2">
              {deliveredSales}
            </span>
          </div>
          {/* Marca de agua */}
          <div className="absolute right-2.5 bottom-2.5 opacity-15 text-emerald-600 pointer-events-none">
            <PackageCheck className="w-12 h-12 stroke-[1.2]" />
          </div>
        </div>

        {/* 4. CANCELADA */}
        <div 
          onClick={() => onSelectFilterState && onSelectFilterState('CANCELLED')}
          className={`relative bg-white rounded-2xl p-4 border transition cursor-pointer overflow-hidden shadow-sm hover:shadow-md ${
            selectedFilterState === 'CANCELLED'
              ? 'border-rose-500 ring-2 ring-rose-500/20 bg-rose-50/20'
              : 'border-slate-200/90 hover:border-slate-300'
          }`}
        >
          <div className="flex flex-col justify-between h-full relative z-10">
            <span className="text-[11px] font-bold tracking-wider text-rose-600 uppercase">
              Cancelada
            </span>
            <span className="text-2xl md:text-3xl font-extrabold text-slate-800 mt-2">
              {cancelledSales}
            </span>
          </div>
          {/* Marca de agua */}
          <div className="absolute right-2.5 bottom-2.5 opacity-15 text-rose-500 pointer-events-none">
            <Ban className="w-12 h-12 stroke-[1.2]" />
          </div>
        </div>
      </div>

      {/* Tarjeta Financiera Compuesta con 6 Canales y Totalizador (6 columnas en lg) */}
      <div className="lg:col-span-6 bg-white border border-slate-200/90 rounded-2xl p-4 shadow-sm flex flex-col md:flex-row items-stretch justify-between gap-4">
        {/* Cuadrícula de 6 Canales Contables */}
        <div className="flex-1 grid grid-cols-3 gap-x-3 gap-y-2.5">
          {/* Fila 1 */}
          <div className="border border-slate-100 rounded-xl p-2 bg-slate-50/50">
            <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
              Efectivo
            </div>
            <div className="text-xs font-bold text-slate-700 mt-0.5">
              {formatAmount(channels.EFECTIVO)}
            </div>
          </div>

          <div className="border border-slate-100 rounded-xl p-2 bg-slate-50/50">
            <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
              Crédito
            </div>
            <div className="text-xs font-bold text-slate-700 mt-0.5">
              {formatAmount(channels.CREDITO)}
            </div>
          </div>

          <div className="border border-slate-100 rounded-xl p-2 bg-slate-50/50">
            <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
              Débito
            </div>
            <div className="text-xs font-bold text-slate-700 mt-0.5">
              {formatAmount(channels.DEBITO)}
            </div>
          </div>

          {/* Fila 2 */}
          <div className="border border-slate-100 rounded-xl p-2 bg-slate-50/50">
            <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
              Transf.
            </div>
            <div className="text-xs font-bold text-slate-700 mt-0.5">
              {formatAmount(channels.TRANSFERENCIA)}
            </div>
          </div>

          <div className="border border-slate-100 rounded-xl p-2 bg-slate-50/50">
            <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
              MercadoPago
            </div>
            <div className="text-xs font-bold text-slate-700 mt-0.5">
              {formatAmount(channels.MERCADOPAGO)}
            </div>
          </div>

          <div className="border border-slate-100 rounded-xl p-2 bg-slate-50/50">
            <div className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase">
              Otro
            </div>
            <div className="text-xs font-bold text-slate-700 mt-0.5">
              {formatAmount(channels.OTRO)}
            </div>
          </div>
        </div>

        {/* Divisor Vertical */}
        <div className="hidden md:block w-px bg-slate-200/80 my-1 self-stretch" />

        {/* Panel Derecho: Totalizador COBRADO DE ESTOS PEDIDOS */}
        <div className="flex md:flex-col justify-between md:justify-center items-end md:items-start pl-0 md:pl-2 min-w-[170px]">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold tracking-wider text-[#0e442f] uppercase">
              Cobrado de estos pedidos
            </span>
            <button
              onClick={() => setShowAmounts(!showAmounts)}
              className="text-slate-400 hover:text-slate-600 transition p-0.5"
              title={showAmounts ? 'Ocultar importes' : 'Mostrar importes'}
            >
              {showAmounts ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            </button>
          </div>
          <div className="text-2xl md:text-[28px] font-black text-[#047857] tracking-tight mt-1">
            {formatGrandTotal(grandTotal)}
          </div>
        </div>
      </div>
    </div>
  );
}
